/**
 * 后台 - 未引用渠道批量清理弹窗
 *
 * 展示所有未被任何视频模型引用的渠道（含已下线 provider 的渠道），
 * 勾选后批量删除。删除前服务端会逐个复检引用关系，被引用的自动跳过。
 */

import { useEffect, useState } from 'react';
import { AlertTriangle, Loader2, Trash2, X } from 'lucide-react';
import {
  videoChannelAdminApi,
  type VideoChannelOrphanView,
} from '../services/videoAdminApi';
import toast from '../../../components/Toast';

interface Props {
  onClose: (changed: boolean) => void;
}

export default function OrphanChannelCleanupModal({ onClose }: Props) {
  const [orphans, setOrphans] = useState<VideoChannelOrphanView[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    videoChannelAdminApi
      .listOrphans()
      .then((data) => {
        if (cancelled) return;
        setOrphans(data);
        setSelectedIds(new Set(data.map((c) => c.id)));
      })
      .catch((err) => {
        toast.error(err instanceof Error ? err.message : '获取未引用渠道失败');
        if (!cancelled) onClose(false);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allSelected = orphans.length > 0 && selectedIds.size === orphans.length;

  const toggleAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(orphans.map((c) => c.id)));
  };

  const handlePurge = async () => {
    if (selectedIds.size === 0) {
      toast.error('请先勾选要清理的渠道');
      return;
    }
    if (!confirm(`确定删除所选 ${selectedIds.size} 个未引用渠道吗？渠道密钥将一并删除，无法恢复。`)) {
      return;
    }
    setSubmitting(true);
    try {
      const result = await videoChannelAdminApi.purgeOrphans([...selectedIds]);
      const { deleted, skipped } = result;
      if (deleted.length > 0) {
        toast.success(`已删除 ${deleted.length} 个渠道`);
      }
      for (const item of skipped) {
        toast.error(`已跳过「${item.name}」：${item.reason}`);
      }
      onClose(deleted.length > 0 || skipped.length > 0);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '批量清理失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-12 px-4"
      onClick={() => onClose(false)}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-light-border flex items-center justify-between">
          <h3 className="text-lg font-semibold text-light-text">清理未引用渠道</h3>
          <button onClick={() => onClose(false)} className="p-1.5 rounded-lg hover:bg-light-hover">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2.5 text-sm text-amber-800">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              以下渠道未被任何视频模型引用。删除后渠道及其 API Key 将一并移除，无法恢复；
              历史任务记录不受影响，但任务对应的渠道名称将无法再解析。
            </span>
          </div>

          {loading ? (
            <div className="py-12 text-center text-light-muted">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              加载中
            </div>
          ) : orphans.length === 0 ? (
            <div className="py-12 text-center text-light-muted text-sm">
              没有未引用的渠道，无需清理
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <span className="text-sm text-light-muted">
                  共 {orphans.length} 个未引用渠道
                </span>
                <button
                  onClick={toggleAll}
                  className="text-sm text-violet-600 hover:text-violet-700"
                >
                  {allSelected ? '取消全选' : '全选'}
                </button>
              </div>

              <div className="divide-y divide-light-border border border-light-border rounded-xl overflow-hidden">
                {orphans.map((c) => (
                  <label
                    key={c.id}
                    className="flex items-start gap-3 px-4 py-3 hover:bg-light-hover/50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(c.id)}
                      onChange={() => toggle(c.id)}
                      className="mt-1 w-4 h-4 accent-violet-600"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-light-text">{c.name}</span>
                        <span className="inline-flex px-2 py-0.5 rounded-full text-xs bg-light-hover text-light-secondary">
                          {c.providerLabel}
                        </span>
                        {!c.isActive && (
                          <span className="inline-flex px-2 py-0.5 rounded-full text-xs bg-zinc-100 text-zinc-600">
                            已禁用
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-light-muted">
                        <span className="truncate max-w-[220px]" title={c.apiUrl}>
                          {c.apiUrl}
                        </span>
                        <span className="font-mono break-all">{c.apiModelId}</span>
                        {c.jobCount > 0 && <span>历史任务 {c.jobCount} 条</span>}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </>
          )}
        </div>

        {!loading && orphans.length > 0 && (
          <div className="px-6 py-4 border-t border-light-border flex items-center justify-end gap-3">
            <button onClick={() => onClose(false)} className="btn-secondary">
              取消
            </button>
            <button
              onClick={handlePurge}
              disabled={submitting || selectedIds.size === 0}
              className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              删除所选{selectedIds.size > 0 ? `（${selectedIds.size}）` : ''}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
